import React, { useState, useEffect } from 'react';
import axios from 'axios';
import authService from '../../utils/authService';
import '../../styles/AdminHomeContent.css';

const AdminHomeContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = authService.getToken();
      const response = await axios.get('/api/admin/home-content', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const contentData = response.data.content;
        // Initialize settings if not present
        if (!contentData.settings) {
          contentData.settings = { blogEnabled: false };
        }
        setContent(contentData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setContent({
        hero: {
          title: { pt: '', en: '' },
          subtitle: { pt: '', en: '' },
          ctaText: { pt: '', en: '' }
        },
        sectionTitles: {
          features: { pt: 'Como Posso Ajudar', en: 'How I Can Help' },
          services: { pt: 'Serviços em Destaque', en: 'Featured Services' },
          testimonials: { pt: 'O Que Dizem os Clientes', en: 'What Clients Say' }
        },
        features: [],
        testimonials: [],
        cta: {
          title: { pt: '', en: '' },
          description: { pt: '', en: '' }
        },
        settings: {
          blogEnabled: false
        }
      });
      setLoading(false);
    }
  };

  const handleSectionTitleChange = (section, lang, value) => {
    setContent(prev => ({
      ...prev,
      sectionTitles: {
        ...prev.sectionTitles,
        [section]: {
          ...prev.sectionTitles[section],
          [lang]: value
        }
      }
    }));
  };

  const handleHeroChange = (field, lang, value) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        [field]: {
          ...prev.hero[field],
          [lang]: value
        }
      }
    }));
  };

  const handleCTAChange = (field, lang, value) => {
    setContent(prev => ({
      ...prev,
      cta: {
        ...prev.cta,
        [field]: {
          ...prev.cta[field],
          [lang]: value
        }
      }
    }));
  };

  const handleFeatureChange = (index, field, lang, value) => {
    const newFeatures = [...content.features];
    if (field === 'icon') {
      newFeatures[index].icon = value;
    } else {
      newFeatures[index][field][lang] = value;
    }
    setContent(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [
        ...prev.features,
        {
          icon: '✨',
          title: { pt: '', en: '' },
          description: { pt: '', en: '' },
          order: prev.features.length + 1
        }
      ]
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleTestimonialChange = (index, field, lang, value) => {
    const newTestimonials = [...content.testimonials];
    if (field === 'name' || field === 'initial') {
      newTestimonials[index][field] = value;
    } else {
      newTestimonials[index][field][lang] = value;
    }
    setContent(prev => ({
      ...prev,
      testimonials: newTestimonials
    }));
  };

  const addTestimonial = () => {
    setContent(prev => ({
      ...prev,
      testimonials: [
        ...prev.testimonials,
        {
          name: '',
          initial: '',
          text: { pt: '', en: '' },
          order: prev.testimonials.length + 1
        }
      ]
    }));
  };

  const removeTestimonial = (index) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = authService.getToken();
      const response = await axios.put('/api/admin/home-content', content, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Home page content updated successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage({ type: 'error', text: 'Failed to save content. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }

    setSaving(false);
  };

  if (loading) {
    return <div className="admin-loading">Loading home page content...</div>;
  }

  if (!content) {
    return <div className="admin-loading">No content found. Please refresh.</div>;
  }

  return (
    <div className="admin-home-content">
      <div className="admin-header">
        <div>
          <h1>Home Page Content</h1>
          <p>Manage your homepage hero, features, testimonials and CTA</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
          Preview Site →
        </a>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="home-content-form">
        {/* Hero Section */}
        <div className="form-section">
          <h2>🎯 Hero Section</h2>
          <p className="section-description">The main banner at the top of your homepage</p>

          <div className="form-group">
            <label>Title (Portuguese)</label>
            <input
              type="text"
              value={content.hero?.title?.pt || ''}
              onChange={(e) => handleHeroChange('title', 'pt', e.target.value)}
              placeholder="Ilumine o Seu Caminho"
            />
          </div>

          <div className="form-group">
            <label>Title (English)</label>
            <input
              type="text"
              value={content.hero?.title?.en || ''}
              onChange={(e) => handleHeroChange('title', 'en', e.target.value)}
              placeholder="Illuminate Your Path"
            />
          </div>

          <div className="form-group">
            <label>Subtitle (Portuguese)</label>
            <textarea
              value={content.hero?.subtitle?.pt || ''}
              onChange={(e) => handleHeroChange('subtitle', 'pt', e.target.value)}
              rows="2"
              placeholder="Orientação espiritual através do Tarot, Astrologia e Cura com Cristais"
            />
          </div>

          <div className="form-group">
            <label>Subtitle (English)</label>
            <textarea
              value={content.hero?.subtitle?.en || ''}
              onChange={(e) => handleHeroChange('subtitle', 'en', e.target.value)}
              rows="2"
              placeholder="Spiritual guidance through Tarot, Astrology, and Crystal Healing"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Button Text (Portuguese)</label>
              <input
                type="text"
                value={content.hero?.ctaText?.pt || ''}
                onChange={(e) => handleHeroChange('ctaText', 'pt', e.target.value)}
                placeholder="Agendar Consulta"
              />
            </div>

            <div className="form-group">
              <label>Button Text (English)</label>
              <input
                type="text"
                value={content.hero?.ctaText?.en || ''}
                onChange={(e) => handleHeroChange('ctaText', 'en', e.target.value)}
                placeholder="Book a Session"
              />
            </div>
          </div>
        </div>

        {/* Section Titles */}
        <div className="form-section">
          <h2>📝 Section Titles</h2>
          <p className="section-description">Titles for the main sections on your homepage</p>

          <div className="form-row">
            <div className="form-group">
              <label>Features Section Title (Portuguese)</label>
              <input
                type="text"
                value={content.sectionTitles?.features?.pt || ''}
                onChange={(e) => handleSectionTitleChange('features', 'pt', e.target.value)}
                placeholder="Como Posso Ajudar"
              />
            </div>

            <div className="form-group">
              <label>Features Section Title (English)</label>
              <input
                type="text"
                value={content.sectionTitles?.features?.en || ''}
                onChange={(e) => handleSectionTitleChange('features', 'en', e.target.value)}
                placeholder="How I Can Help"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Services Section Title (Portuguese)</label>
              <input
                type="text"
                value={content.sectionTitles?.services?.pt || ''}
                onChange={(e) => handleSectionTitleChange('services', 'pt', e.target.value)}
                placeholder="Serviços em Destaque"
              />
            </div>

            <div className="form-group">
              <label>Services Section Title (English)</label>
              <input
                type="text"
                value={content.sectionTitles?.services?.en || ''}
                onChange={(e) => handleSectionTitleChange('services', 'en', e.target.value)}
                placeholder="Featured Services"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Testimonials Section Title (Portuguese)</label>
              <input
                type="text"
                value={content.sectionTitles?.testimonials?.pt || ''}
                onChange={(e) => handleSectionTitleChange('testimonials', 'pt', e.target.value)}
                placeholder="O Que Dizem os Clientes"
              />
            </div>

            <div className="form-group">
              <label>Testimonials Section Title (English)</label>
              <input
                type="text"
                value={content.sectionTitles?.testimonials?.en || ''}
                onChange={(e) => handleSectionTitleChange('testimonials', 'en', e.target.value)}
                placeholder="What Clients Say"
              />
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="form-section">
          <h2>✨ Features Section</h2>
          <p className="section-description">The 3 feature cards below the hero</p>

          {content.features?.map((feature, index) => (
            <div key={index} className="feature-item">
              <div className="item-header">
                <h3>Feature {index + 1}</h3>
                {content.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>Icon (Emoji)</label>
                <input
                  type="text"
                  value={feature.icon || ''}
                  onChange={(e) => handleFeatureChange(index, 'icon', null, e.target.value)}
                  placeholder="🔮"
                  maxLength="2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Title (Portuguese)</label>
                  <input
                    type="text"
                    value={feature.title?.pt || ''}
                    onChange={(e) => handleFeatureChange(index, 'title', 'pt', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Title (English)</label>
                  <input
                    type="text"
                    value={feature.title?.en || ''}
                    onChange={(e) => handleFeatureChange(index, 'title', 'en', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description (Portuguese)</label>
                <textarea
                  value={feature.description?.pt || ''}
                  onChange={(e) => handleFeatureChange(index, 'description', 'pt', e.target.value)}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Description (English)</label>
                <textarea
                  value={feature.description?.en || ''}
                  onChange={(e) => handleFeatureChange(index, 'description', 'en', e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          ))}

          <button type="button" onClick={addFeature} className="btn btn-secondary">
            + Add Feature
          </button>
        </div>

        {/* Testimonials Section */}
        <div className="form-section">
          <h2>💬 Testimonials</h2>
          <p className="section-description">Customer reviews and testimonials</p>

          {content.testimonials?.map((testimonial, index) => (
            <div key={index} className="testimonial-item">
              <div className="item-header">
                <h3>Testimonial {index + 1}</h3>
                {content.testimonials.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTestimonial(index)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name</label>
                  <input
                    type="text"
                    value={testimonial.name || ''}
                    onChange={(e) => handleTestimonialChange(index, 'name', null, e.target.value)}
                    placeholder="Maria Silva"
                  />
                </div>

                <div className="form-group">
                  <label>Initial (for avatar)</label>
                  <input
                    type="text"
                    value={testimonial.initial || ''}
                    onChange={(e) => handleTestimonialChange(index, 'initial', null, e.target.value)}
                    placeholder="M"
                    maxLength="1"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Testimonial Text (Portuguese)</label>
                <textarea
                  value={testimonial.text?.pt || ''}
                  onChange={(e) => handleTestimonialChange(index, 'text', 'pt', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Testimonial Text (English)</label>
                <textarea
                  value={testimonial.text?.en || ''}
                  onChange={(e) => handleTestimonialChange(index, 'text', 'en', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          ))}

          <button type="button" onClick={addTestimonial} className="btn btn-secondary">
            + Add Testimonial
          </button>
        </div>

        {/* CTA Section */}
        <div className="form-section">
          <h2>🚀 Call to Action (CTA)</h2>
          <p className="section-description">The final call to action at the bottom of the page</p>

          <div className="form-group">
            <label>Title (Portuguese)</label>
            <input
              type="text"
              value={content.cta?.title?.pt || ''}
              onChange={(e) => handleCTAChange('title', 'pt', e.target.value)}
              placeholder="Pronta Para Começar?"
            />
          </div>

          <div className="form-group">
            <label>Title (English)</label>
            <input
              type="text"
              value={content.cta?.title?.en || ''}
              onChange={(e) => handleCTAChange('title', 'en', e.target.value)}
              placeholder="Ready to Begin?"
            />
          </div>

          <div className="form-group">
            <label>Description (Portuguese)</label>
            <textarea
              value={content.cta?.description?.pt || ''}
              onChange={(e) => handleCTAChange('description', 'pt', e.target.value)}
              rows="2"
              placeholder="Agende a sua consulta hoje..."
            />
          </div>

          <div className="form-group">
            <label>Description (English)</label>
            <textarea
              value={content.cta?.description?.en || ''}
              onChange={(e) => handleCTAChange('description', 'en', e.target.value)}
              rows="2"
              placeholder="Book your consultation today..."
            />
          </div>
        </div>

        {/* Site Settings */}
        <div className="form-section">
          <h2>⚙️ Site Settings</h2>
          <p className="section-description">Control site-wide features</p>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={content.settings?.blogEnabled || false}
                onChange={(e) => setContent(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    blogEnabled: e.target.checked
                  }
                }))}
              />
              Enable Blog Page (shows Blog link in navbar and footer)
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminHomeContent;
